/** Planejamento de mapa e de tabela, sem desenhar o PDF. */

export interface MapFrame {
  x: number;
  y: number;
  width: number;
  height: number;
  continued: boolean;
}

export interface MapLayout {
  orientation: 'landscape';
  scale: number;
  frames: MapFrame[];
  split: boolean;
}

export const MAP_DRAW = { originX: 14, originY: 24, availW: 269, availH: 168 };
const CARD_PX = 220;
const MIN_CARD_MM = 18;

export function planMapLayout(contentWidthPx: number, contentHeightPx: number): MapLayout {
  const width = Math.max(contentWidthPx, 1);
  const height = Math.max(contentHeightPx, 1);
  const fit = Math.min(MAP_DRAW.availW / width, MAP_DRAW.availH / height);
  const minScale = MIN_CARD_MM / CARD_PX;
  if (fit >= minScale) {
    return {
      orientation: 'landscape',
      scale: fit,
      split: false,
      frames: [{ x: 0, y: 0, width, height, continued: false }],
    };
  }
  const tileW = MAP_DRAW.availW / minScale;
  const tileH = MAP_DRAW.availH / minScale;
  const frames: MapFrame[] = [];
  for (let y = 0; y < height - 0.5; y += tileH) {
    for (let x = 0; x < width - 0.5; x += tileW) {
      frames.push({
        x,
        y,
        width: Math.min(tileW, width - x),
        height: Math.min(tileH, height - y),
        continued: true,
      });
    }
  }
  if (frames.length > 0) frames[frames.length - 1].continued = false;
  return { orientation: 'landscape', scale: minScale, split: frames.length > 1, frames };
}

export function physicalPageLabels(total: number): string[] {
  return Array.from({ length: Math.max(0, total) }, (_, index) => `${index + 1} de ${total}`);
}

export function mapPageFill(layout: MapLayout): number {
  const frame = layout.frames[0];
  if (!frame) return 0;
  const drawnW = frame.width * layout.scale;
  const drawnH = frame.height * layout.scale;
  return Math.max(drawnW / MAP_DRAW.availW, drawnH / MAP_DRAW.availH);
}

export function mapFramesCover(contentWidthPx: number, contentHeightPx: number, layout: MapLayout): boolean {
  if (layout.frames.length === 0) return contentWidthPx <= 0 && contentHeightPx <= 0;
  const maxRight = Math.max(...layout.frames.map((frame) => frame.x + frame.width));
  const maxBottom = Math.max(...layout.frames.map((frame) => frame.y + frame.height));
  return maxRight + 0.5 >= contentWidthPx && maxBottom + 0.5 >= contentHeightPx;
}

export interface TablePage<T> {
  rows: T[];
  continued: boolean;
}

/** Cada linha permanece inteira. A página seguinte repete o cabeçalho no renderer. */
export function paginateRows<T>(rows: T[], firstCapacity: number, nextCapacity: number): TablePage<T>[] {
  if (rows.length === 0) return [];
  const pages: TablePage<T>[] = [];
  let index = 0;
  let capacity = Math.max(1, firstCapacity);
  while (index < rows.length) {
    const take = Math.min(capacity, rows.length - index);
    const slice = rows.slice(index, index + take);
    index += take;
    pages.push({ rows: slice, continued: index < rows.length });
    capacity = Math.max(1, nextCapacity);
  }
  return pages;
}
